// DS row 116 Approvals and ApprovalRow: the resolved approvals of an offer package (O-4, O-8). One compact row per
// applicable approval: the role as a tag (*HM* · *HRBP* · *Organizational leader* · *Total Rewards*), the person, the
// state pill (*Pending · Approved · Rejected · Revision Required*), the package version in mono, the *Evidence* link
// and, where another seat obtained it, the note *obtained by HM*. A non-applicable approval is not drawn (can={false},
// or simply omitted); there is no empty state, because the list always holds the required approvals.

import { Children, isValidElement, type ReactNode } from "react";
import { Person, Pill, Tag, TextLink } from "../core";
import { cx } from "../lib/cx";
import { G6_LABELS, G6_TEMPLATES } from "./labels";
import type { Hs } from "../lib/hs";

export interface ApprovalRowProps {
  /** The approving role's words (a label). */
  role: string;
  person: { name: string; hueKey?: string | undefined };
  /** The state word (*Pending · Approved · Rejected · Revision Required*); its tone follows the word. */
  state: string;
  /** The package version (*v2*), in mono. */
  version?: string | undefined;
  /** The approval evidence: a link to the stored file. */
  evidence?: string | undefined;
  /** The role that obtained this approval (*HM*): the note "obtained by HM". */
  obtainedBy?: string | undefined;
  /** false: this approval does not apply to the package; the row is not drawn. */
  can?: boolean | undefined;
  hs?: Hs | undefined;
}

export function ApprovalRow({ role, person, state, version, evidence, obtainedBy, can, hs }: ApprovalRowProps) {
  if (can === false) return null;
  return (
    <li className="mc-aprow">
      <span className="mc-aprow-role"><Tag>{role}</Tag></span>
      <span className="mc-aprow-p">
        <Person name={person.name} hueKey={person.hueKey} size={24} sub={obtainedBy ? G6_TEMPLATES.obtainedBy({ role: obtainedBy }) : undefined} />
      </span>
      <span className="mc-aprow-s"><Pill>{state}</Pill></span>
      <span className="mc-aprow-v mc-mono">{version ?? ""}</span>
      <span className="mc-aprow-e">{evidence ? <TextLink href={evidence} icon="file" hs={hs}>{G6_LABELS.evidence}</TextLink> : null}</span>
    </li>
  );
}

export interface ApprovalsProps {
  /** ApprovalRow elements; rows that do not apply render nothing and are not counted. */
  children: ReactNode;
  /** The list's name (a label), when no heading names it. */
  label?: string | undefined;
  labelledBy?: string | undefined;
  className?: string | undefined;
}

export function Approvals({ children, label, labelledBy, className }: ApprovalsProps) {
  const rows = Children.toArray(children).filter((child) => !(isValidElement<ApprovalRowProps>(child) && child.props.can === false));
  return (
    <ul className={cx("mc-approvals", className)} aria-label={labelledBy ? undefined : label} aria-labelledby={labelledBy}>
      {rows}
    </ul>
  );
}
